import { useState, useEffect, createContext, useContext, lazy, Suspense, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { RefreshCw } from "lucide-react";

// Pages - lazy loaded for faster initial load
import Login from "./pages/Login";
import Layout from "./components/Layout";
import AdminLogin from "./pages/AdminLogin";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Entregas = lazy(() => import("./pages/Entregas"));
const Encomendas = lazy(() => import("./pages/Encomendas"));
const Unidades = lazy(() => import("./pages/Unidades"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Acessos = lazy(() => import("./pages/Acessos"));
const Conta = lazy(() => import("./pages/Conta"));
const EsqueciSenha = lazy(() => import("./pages/EsqueciSenha"));
const NovaSenha = lazy(() => import("./pages/NovaSenha"));
const AdminConta = lazy(() => import("./pages/admin/AdminConta"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminCondominioDetails = lazy(() => import("./pages/AdminCondominioDetails"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const PainelClaro = ({ children }) => (<AdminLayout><div className="rounded-xl bg-slate-50 text-slate-900 p-4 sm:p-6">{children}</div></AdminLayout>);
const Alerts = lazy(() => import("./pages/admin/Alerts"));
const PublicPickup = lazy(() => import("./pages/PublicPickup"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Token em todas as chamadas ───────────────────────────────────────────
axios.interceptors.request.use((config) => {
  const naAreaAdmin = window.location.pathname.startsWith("/admin");
  const token = naAreaAdmin
    ? localStorage.getItem("adminToken")
    : (localStorage.getItem("token") || localStorage.getItem("adminToken"));
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Sessão expirada + nova tentativa em falha de rede (3x) ────────────────
axios.interceptors.response.use(
  res => res,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    const status = error.response?.status;
    if (status === 401 && !String(config.url || "").includes("/auth/login") && !String(config.url || "").includes("/admin/verify-code")) {
      if (window.location.pathname.startsWith("/admin")) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("superAdmin");
        if (window.location.pathname !== "/admin") window.location.href = "/admin";
      } else if (localStorage.getItem("token")) {
        ["token", "condominio", "usuario", "permissoes", "condominios"].forEach(k => localStorage.removeItem(k));
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
    config._retryCount = config._retryCount || 0;
    const isRetryable = !error.response || error.response.status >= 500;
    if (isRetryable && config._retryCount < 3) {
      config._retryCount += 1;
      await new Promise(r => setTimeout(r, 1000 * config._retryCount));
      return axios(config);
    }
    return Promise.reject(error);
  }
);
// ────────────────────────────────────────────────────────────────────────

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const lerJSON = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };

// sessões antigas (antes do login com senha) não têm token: limpa
if (!localStorage.getItem("token")) {
  ["condominio", "usuario", "permissoes"].forEach(k => localStorage.removeItem(k));
}

const AuthProvider = ({ children }) => {
  const [condominio, setCondominio] = useState(() => lerJSON("condominio"));
  const [usuario, setUsuario] = useState(() => lerJSON("usuario"));
  const [permissoes, setPermissoes] = useState(() => lerJSON("permissoes") || {});
  const [condominios, setCondominios] = useState(() => lerJSON("condominios") || []);
  const [loading, setLoading] = useState(false);

  // só troca o estado quando algo mudou de verdade (senão a tela recarrega à toa a cada minuto e "pisca")
  const igual = (x, y) => JSON.stringify(x) === JSON.stringify(y);
  const guardar = (c, u, p, lista) => {
    setCondominio((v) => (igual(v, c) ? v : c));
    setUsuario((v) => (igual(v, u) ? v : u));
    setPermissoes((v) => (igual(v, p || {}) ? v : (p || {})));
    localStorage.setItem("condominio", JSON.stringify(c));
    localStorage.setItem("usuario", JSON.stringify(u));
    localStorage.setItem("permissoes", JSON.stringify(p || {}));
    if (lista) { setCondominios((v) => (igual(v, lista) ? v : lista)); localStorage.setItem("condominios", JSON.stringify(lista)); }
  };

  // busca perfil, condomínio e permissões atualizados (vale na hora o que o admin mudar)
  const refreshCondominio = useCallback(async () => {
    if (!localStorage.getItem("token")) return;
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      if (data?.tipo === "usuario") guardar(data.condominio, { ...data.usuario, cargo: data.cargo }, data.permissoes, data.condominios);
    } catch (error) {
      console.warn("Falha ao atualizar sessão:", error.message);
    }
  }, []);

  useEffect(() => {
    refreshCondominio();
    const t = setInterval(refreshCondominio, 60 * 1000);
    const onFocus = () => refreshCondominio();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [refreshCondominio]);

  const login = async (usuarioLogin, senha) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { usuario: usuarioLogin, senha });
      localStorage.setItem("token", data.token);
      toast.success(`Bem-vindo, ${data.usuario?.nome?.split(" ")[0] || ""}!`);
      // síndico de vários condomínios: só entra depois de escolher (a tela de login mostra a lista)
      if ((data.condominios || []).length > 1) return { success: true, cargo: data.cargo, condominios: data.condominios, resposta: data };
      guardar(data.condominio, { ...data.usuario, cargo: data.cargo }, data.permissoes, data.condominios);
      return { success: true, cargo: data.cargo, condominios: data.condominios || [] };
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao fazer login";
      toast.error(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  // depois do "primeiro acesso" o servidor já devolve a sessão pronta
  const entrarComResposta = (data, aviso = true) => {
    localStorage.setItem("token", data.token);
    guardar(data.condominio, { ...data.usuario, cargo: data.cargo }, data.permissoes, data.condominios);
    if (aviso) toast.success(`Pronto, ${data.usuario?.nome?.split(" ")[0] || ""}! Seu acesso foi criado.`);
  };

  // síndico de vários condomínios: troca sem sair (o servidor devolve um token para o condomínio escolhido)
  const trocarCondominio = async (id, recarregar = true) => {
    const { data } = await axios.post(`${API}/auth/trocar-condominio`, { condominio_id: id });
    entrarComResposta(data, false);
    if (recarregar) window.location.href = "/";
  };

  const logout = () => {
    setCondominio(null); setUsuario(null); setPermissoes({});
    ["token", "condominio", "usuario", "permissoes", "condominios"].forEach(k => localStorage.removeItem(k));
    setCondominios([]);
    toast.info("Você saiu do sistema.");
  };

  const pode = useCallback((perm) => permissoes?.[perm] === true, [permissoes]);

  return (
    <AuthContext.Provider value={{ condominio, usuario, permissoes, condominios, pode, login, logout, loading, refreshCondominio, entrarComResposta, trocarCondominio }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route for regular users
const ProtectedRoute = ({ children }) => {
  const { condominio } = useAuth();
  const location = useLocation();

  if (!condominio || !localStorage.getItem("token")) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Página só para quem tem a permissão
const ComPermissao = ({ perm, children }) => {
  const { pode } = useAuth();
  if (pode(perm)) return children;
  return (
    <div className="max-w-md mx-auto mt-16 text-center bg-white border border-slate-200 rounded-xl p-8">
      <h2 className="font-heading text-2xl text-slate-900 mb-2">SEM PERMISSÃO</h2>
      <p className="text-slate-500">Seu perfil não tem acesso a esta tela. Fale com o administrador do sistema.</p>
    </div>
  );
};

function AppRoutes() {
  const { condominio } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/conta" element={<AdminConta />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/nova-senha" element={<NovaSenha />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/condominio/:id" element={<AdminCondominioDetails />} />
        <Route path="/admin/audit-logs" element={<PainelClaro><AuditLogs /></PainelClaro>} />
        <Route path="/admin/alerts" element={<PainelClaro><Alerts /></PainelClaro>} />
        
        {/* Regular User Routes */}
        <Route
          path="/login"
          element={condominio ? <Navigate to="/" replace /> : <Login />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="entregas" element={<ComPermissao perm="registrar_entregas"><Entregas /></ComPermissao>} />
          <Route path="encomendas" element={<ComPermissao perm="ver_encomendas"><Encomendas /></ComPermissao>} />
          <Route path="unidades" element={<ComPermissao perm="ver_unidades"><Unidades /></ComPermissao>} />
          <Route path="relatorios" element={<ComPermissao perm="ver_relatorios"><Relatorios /></ComPermissao>} />
          <Route path="acessos" element={<ComPermissao perm="gerenciar_acessos"><Acessos /></ComPermissao>} />
          <Route path="conta" element={<Conta />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const currentVersion = useRef(null);

  useEffect(() => {
    // Atualiza sozinho quando sai versão nova: espera a pessoa não estar no meio de algo
    // (janela aberta ou digitando) e recarrega a página.
    let esperando = null;
    const ocupado = () => {
      if (document.querySelector('[role="dialog"],[role="alertdialog"]')) return true;
      const a = document.activeElement;
      return !!a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !!a.value;
    };
    const recarregar = () => {
      if (esperando) return;
      setNewVersionAvailable(true);
      const tentar = () => {
        if (document.hidden || !ocupado()) { window.location.reload(); return; }
        esperando = setTimeout(tentar, 10000);
      };
      esperando = setTimeout(tentar, 2500);
    };
    const checkVersion = async () => {
      try {
        const res = await axios.get(`${API}/version`, { params: { t: Date.now() } });
        const v = res.data.version;
        if (currentVersion.current && v !== currentVersion.current) recarregar();
        else currentVersion.current = v;
      } catch {
        // silently ignore version check failures
      }
    };
    checkVersion();
    const interval = setInterval(checkVersion, 2 * 60 * 1000); // 2 min
    const aoVoltar = () => { if (!document.hidden) checkVersion(); };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => { clearInterval(interval); clearTimeout(esperando); document.removeEventListener("visibilitychange", aoVoltar); };
  }, []);

  return (
    <BrowserRouter>
      <div className="App min-h-screen bg-slate-50">
        {window.location.hostname.startsWith("teste") && (
          <div className="bg-red-600 text-white text-center text-xs font-bold tracking-wide py-1 no-print">
            AMBIENTE DE TESTE — mensagens só vão para os números de teste
          </div>
        )}
        {newVersionAvailable && (
          <div className="fixed top-0 left-0 right-0 z-[100] bg-blue-600 text-white text-sm py-2 px-4 flex items-center justify-between shadow-lg">
            <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Sistema atualizado! Recarregando…</span>
            <button
              onClick={() => window.location.reload(true)}
              className="flex items-center gap-1.5 bg-white text-blue-700 font-semibold text-xs px-3 py-1.5 rounded-full hover:bg-blue-50 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Atualizar agora
            </button>
          </div>
        )}
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{ className: "font-body" }}
        />
        {/* Public route outside AuthProvider to avoid re-renders */}
        <Routes>
          <Route path="/retirada/:codigo" element={
            <Suspense fallback={<PageLoader />}>
              <PublicPickup />
            </Suspense>
          } />
          <Route path="*" element={
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          } />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
